import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatMoney,
  fromCents,
  type LedgerCycle,
  type LedgerYear,
  OPENING_STATE_LABELS,
  openingYearOf,
  type OpeningAccountResponse,
  type OpeningResponse,
  type OpeningState,
  toCents,
} from '@k-management/shared';
import { Fragment, useState } from 'react';
import { Link } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Icon } from '../../components/Icon.js';
import { type Command, useCommands, usePageTitle } from '../../components/layout/page-chrome.js';
import { amountSort, SortHeader, useTableSort } from '../../components/SortHeader.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate, formatTimestamp } from '../../lib/format.js';
import { downloadText } from '../tables/table-format.js';
import { LinkDialog, newSubjectFromLedger } from './LedgersPage.js';
import { OpeningMoveDialog } from './OpeningMoveDialog.js';
import { useSessionValue } from '../../lib/preferences.js';

const CYCLE_TABS: [LedgerCycle, string][] = [
  ['passivo', 'Fornitori (debiti)'],
  ['attivo', 'Clienti (crediti)'],
];

const STATE_PILL: Record<OpeningState, string> = {
  ricostruito: 'pill ok',
  'da-controllare': 'pill warn',
  'senza-dettaglio': 'pill muted',
};

/** The 1 January shown first: the one fixed, otherwise by the rule of the Tesoreria. */
function defaultYear(years: LedgerYear[]): number | null {
  return (
    years.find((item) => item.openingFixed)?.year ?? openingYearOf(years.map((item) => item.year))
  );
}

/**
 * Tesoreria › Situazione iniziale (09/10/2026): customers and suppliers with a
 * balance at 1 January, from the opening of the ledger of the year, and the
 * invoices still open, rebuilt from the lines of the year before.
 */
export function OpeningPage() {
  usePageTitle('Situazione iniziale');
  const canWrite = useCan('tesoreria.write');
  const canCreate = useCan('anagrafica.write');
  const years = useQuery({
    queryKey: ['treasury', 'ledgers'],
    queryFn: () => apiRequest<LedgerYear[]>('/treasury/ledgers'),
  });
  const [year, setYear] = useSessionValue<number | null>('list.opening.year', null);
  const shown = year ?? (years.data ? defaultYear(years.data) : null);
  // One situation fixed at a time (1.49).
  const otherFixed = years.data?.find((item) => item.openingFixed && item.year !== shown);
  const [cycle, setCycle] = useSessionValue<LedgerCycle>('list.opening.cycle', 'passivo');
  const [query, setQuery] = useSessionValue('list.opening.query', '');
  const [onlyToCheck, setOnlyToCheck] = useSessionValue('list.opening.onlyToCheck', false);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [linking, setLinking] = useState<OpeningAccountResponse | null>(null);
  const opening = useQuery({
    queryKey: ['treasury', 'ledgers', 'opening', shown ?? 0],
    queryFn: () => apiRequest<OpeningResponse>(`/treasury/ledgers/${shown}/opening`),
    enabled: shown !== null,
  });
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<'fix' | 'unfix' | null>(null);
  // «Sposta la situazione» (2.13): from the 1 January fixed to the one shown.
  const [moving, setMoving] = useState(false);
  // «Fissa» and «Ricalcola dai mastrini» (1.48): the whole Tesoreria reloads behind.
  const fix = useMutation({
    mutationFn: (action: 'fix' | 'unfix') =>
      apiRequest<OpeningResponse>(`/treasury/ledgers/${shown}/opening/fix`, {
        method: action === 'fix' ? 'POST' : 'DELETE',
      }),
    onSuccess: async () => {
      setConfirming(null);
      await queryClient.invalidateQueries({ queryKey: ['treasury'] });
    },
  });

  const ofCycle = (opening.data?.accounts ?? []).filter((account) => account.cycle === cycle);
  const needle = query.trim().toLowerCase();
  const rows = ofCycle.filter(
    (account) =>
      (!onlyToCheck || account.state === 'da-controllare' || !account.subject) &&
      (!needle ||
        [account.name, account.vatNumber, account.taxCode, account.foreignId, account.subject?.name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(needle)),
  );
  // The subjects sort; the open invoices of an expanded one stay under it.
  const sorting = useTableSort('opening', rows, {
    name: (account) => account.name,
    subject: (account) => account.subject?.name ?? null,
    documents: (account) => account.items.filter((item) => item.kind === 'documento').length,
    balance: (account) => amountSort(account.balance),
    state: (account) => OPENING_STATE_LABELS[account.state],
  });
  const sum = (values: string[]) =>
    fromCents(values.reduce((total, value) => total + toCents(value), 0));
  const documents = ofCycle.flatMap((account) =>
    account.items.filter((item) => item.kind === 'documento'),
  );
  const ante = ofCycle.flatMap((account) => account.items.filter((item) => item.kind === 'ante'));
  const toCheck = ofCycle.filter(
    (account) => account.state === 'da-controllare' || !account.subject,
  ).length;

  useCommands(
    opening.data
      ? [
          {
            key: 'export',
            label: 'Esporta',
            icon: 'upload',
            onClick: () =>
              downloadText(
                `situazione-iniziale-${opening.data.year}.csv`,
                toCsv(opening.data.accounts),
              ),
          } satisfies Command,
        ]
      : [],
  );

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>Situazione iniziale{shown !== null && ` al 01/01/${shown}`}</h1>
          <p className="muted">
            Clienti e fornitori con un saldo al 1° gennaio: il saldo è quello di apertura del
            mastrino dell’anno; le fatture aperte sono ricostruite dalle righe del mastrino
            dell’anno prima (gli incassi e i pagamenti chiudono prima la fattura che citano, poi le
            più vecchie). La spunta dice se il mastrino dell’anno conferma l’incasso o il pagamento.
          </p>
        </div>
      </div>

      {years.data && years.data.length === 0 && (
        <p className="notice info">
          Nessun mastrino caricato: si caricano in{' '}
          <Link to="/tesoreria/mastrini">Tesoreria › Mastrini</Link>.
        </p>
      )}
      {years.data && years.data.length > 0 && (
        <div className="ledger-toolbar">
          <div className="segmented-small" role="group" aria-label="Situazione al">
            {[...years.data]
              .sort((a, b) => a.year - b.year)
              .map((item) => (
                <button
                  key={item.year}
                  type="button"
                  className={item.year === shown ? 'selected' : ''}
                  onClick={() => {
                    setYear(item.year);
                    setOpen(new Set());
                  }}
                >
                  01/01/{item.year}
                </button>
              ))}
          </div>
        </div>
      )}

      {opening.data && (
        <>
          <div className="card stack tight">
            {opening.data.fixed ? (
              <>
                <div className="row between">
                  <span>
                    <span className="pill info">Fissata</span> il{' '}
                    {formatTimestamp(opening.data.fixed.at)}
                    {opening.data.fixed.by && ` da ${opening.data.fixed.by}`}:{' '}
                    {opening.data.fixed.items} partite, {opening.data.fixed.withPayments} con
                    incassi o pagamenti collegati.
                  </span>
                  {canWrite && opening.data.fixed.withPayments === 0 && (
                    <button type="button" className="small" onClick={() => setConfirming('unfix')}>
                      Ricalcola dai mastrini
                    </button>
                  )}
                </div>
                <p className="muted small">
                  Le partite si incassano e si pagano come le fatture: dalla riconciliazione dei
                  movimenti o a mano dalla partita. Un nuovo caricamento dei mastrini non le cambia.
                </p>
              </>
            ) : (
              <div className="row between">
                <span className="muted">
                  Calcolata dai mastrini a ogni apertura. Per collegarci incassi e pagamenti va
                  fissata: le partite dei soggetti «da controllare» restano fuori.
                </span>
                {otherFixed ? (
                  <span className="row">
                    <span className="muted small">
                      È fissata la situazione al 01/01/{otherFixed.year}.
                    </span>
                    {canWrite && (
                      <button type="button" className="primary" onClick={() => setMoving(true)}>
                        Sposta qui la situazione…
                      </button>
                    )}
                  </span>
                ) : (
                  canWrite && (
                    <button type="button" className="primary" onClick={() => setConfirming('fix')}>
                      Fissa la situazione al 01/01/{opening.data.year}
                    </button>
                  )
                )}
              </div>
            )}
            {fix.error && <p className="error">{fix.error.message}</p>}
          </div>
          {!opening.data.previousLoaded && (
            <p className="notice small">
              Il mastrino {opening.data.year - 1} non è caricato: ci sono i saldi, ma non le singole
              fatture.
            </p>
          )}
          <nav className="subtabs" aria-label="Ciclo">
            {CYCLE_TABS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={cycle === key ? 'subtab selected' : 'subtab'}
                onClick={() => setCycle(key)}
              >
                {label}
              </button>
            ))}
          </nav>
          <div className="treasury-tiles">
            <div className="treasury-tile">
              <span>{cycle === 'attivo' ? 'Crediti' : 'Debiti'} al 01/01</span>
              <strong>{formatMoney(sum(ofCycle.map((account) => account.balance)))}</strong>
            </div>
            <div className="treasury-tile">
              <span>Soggetti con saldo</span>
              <strong>{ofCycle.length}</strong>
            </div>
            <div className="treasury-tile">
              <span>Fatture aperte</span>
              <strong>{documents.length}</strong>
            </div>
            <div className="treasury-tile">
              <span>Saldo senza dettaglio</span>
              <strong>{formatMoney(sum(ante.map((item) => item.open)))}</strong>
            </div>
            <div className="treasury-tile">
              <span>Da controllare</span>
              <strong>{toCheck}</strong>
            </div>
          </div>

          <div className="ledger-toolbar">
            <label className="search-box">
              <Icon name="search" />
              <input
                value={query}
                placeholder="Soggetto, partita IVA o codice fiscale"
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={onlyToCheck}
                onChange={(event) => setOnlyToCheck(event.target.checked)}
              />
              Solo da controllare o da abbinare
            </label>
          </div>

          <div className="table-wrap">
            <table className="data-table ledger-table">
              <thead>
                <tr>
                  <SortHeader sort={sorting} column="name">
                    Soggetto
                  </SortHeader>
                  <SortHeader sort={sorting} column="subject">
                    Anagrafica
                  </SortHeader>
                  <SortHeader sort={sorting} column="documents" numeric>
                    Fatture aperte
                  </SortHeader>
                  <SortHeader sort={sorting} column="balance" numeric>
                    Saldo al 01/01/{opening.data.year}
                  </SortHeader>
                  <SortHeader sort={sorting} column="state">
                    Stato
                  </SortHeader>
                </tr>
              </thead>
              <tbody>
                {sorting.rows.map((account) => {
                  const expanded = open.has(account.id);
                  return (
                    <Fragment key={account.id}>
                      <tr
                        className="openable"
                        onClick={(event) => {
                          if ((event.target as HTMLElement).closest('button, a')) return;
                          const next = new Set(open);
                          if (expanded) next.delete(account.id);
                          else next.add(account.id);
                          setOpen(next);
                        }}
                      >
                        <td>
                          <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>{' '}
                          <strong>{account.name}</strong>
                          <small className="muted block">
                            {[
                              account.vatNumber ?? account.taxCode ?? account.foreignId,
                              `partitario ${account.partitario}`,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </small>
                        </td>
                        <td>
                          {account.subject ? (
                            <Link to={`/anagrafica/${account.subject.id}`}>
                              {account.subject.name}
                            </Link>
                          ) : (
                            <span className="pill warn">Non abbinato</span>
                          )}
                          {canWrite && (
                            <button
                              type="button"
                              className="link small block"
                              onClick={() => setLinking(account)}
                            >
                              {account.subject ? 'Cambia' : 'Abbina'}
                            </button>
                          )}
                          {canWrite && canCreate && !account.subject && (
                            <Link
                              className="small block"
                              to={newSubjectFromLedger(account, '/tesoreria/situazione-iniziale')}
                            >
                              Crea in Anagrafica
                            </Link>
                          )}
                        </td>
                        <td className="numeric">
                          {account.items.filter((item) => item.kind === 'documento').length}
                        </td>
                        <td className="numeric">
                          <strong>{formatMoney(account.balance)}</strong>
                        </td>
                        <td>
                          <span className={STATE_PILL[account.state]}>
                            {OPENING_STATE_LABELS[account.state]}
                          </span>
                          {account.difference && (
                            <small className="block warn-text">
                              le fatture non tornano di {formatMoney(account.difference)}
                            </small>
                          )}
                          {toCents(account.balance) < 0 && (
                            <small className="block muted">
                              saldo a favore: acconto o nota di credito
                            </small>
                          )}
                        </td>
                      </tr>
                      {expanded && <ItemRows account={account} />}
                    </Fragment>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted">
                      Nessun soggetto con questi filtri.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      {opening.isError && <p className="error">{opening.error.message}</p>}
      {moving && otherFixed && shown !== null && (
        <OpeningMoveDialog
          from={otherFixed.year}
          to={shown}
          onClose={() => setMoving(false)}
          onMoved={() => setMoving(false)}
        />
      )}
      {confirming && opening.data && (
        <ConfirmDialog
          title={
            confirming === 'fix'
              ? `Fissa la situazione al 01/01/${opening.data.year}`
              : 'Ricalcola dai mastrini'
          }
          confirmLabel={confirming === 'fix' ? 'Fissa' : 'Ricalcola'}
          busy={fix.isPending}
          error={fix.error?.message ?? null}
          onConfirm={() => fix.mutate(confirming)}
          onCancel={() => setConfirming(null)}
        >
          {confirming === 'fix' ? (
            <p>
              Le fatture aperte dei soggetti che tornano diventano partite di apertura: si incassano
              e si pagano come le fatture e compaiono nella riconciliazione e nelle schede
              contabili. Restano fuori{' '}
              {opening.data.accounts.filter((account) => account.state === 'da-controllare').length}{' '}
              soggetti da controllare
              {opening.data.accounts.some((account) => !account.subject) &&
                `; ${opening.data.accounts.filter((account) => !account.subject).length} non sono abbinati all’Anagrafica: conviene abbinarli prima`}
              .
            </p>
          ) : (
            <p>
              Le partite fissate vengono tolte e la situazione torna calcolata dai mastrini. Si può
              solo finché nessuna partita ha incassi o pagamenti collegati.
            </p>
          )}
        </ConfirmDialog>
      )}
      {linking && <LinkDialog account={linking} onClose={() => setLinking(null)} />}
    </section>
  );
}

/** The invoices still open of a subject, with what was settled in the year before. */
function ItemRows({ account }: { account: OpeningAccountResponse }) {
  if (account.items.length === 0) {
    return (
      <tr className="ledger-entry">
        <td colSpan={5} className="muted">
          Nessuna fattura aperta: il saldo è a favore, guarda il mastrino.
        </td>
      </tr>
    );
  }
  return (
    <>
      {account.items.map((item, index) => (
        <tr key={index} className="ledger-entry">
          <td colSpan={2}>
            {item.kind === 'ante' ? (
              <>
                {item.invoiceId ? (
                  <Link to={`/tesoreria/fatture/${item.invoiceId}`}>
                    Saldo già aperto a inizio anno precedente
                  </Link>
                ) : (
                  'Saldo già aperto a inizio anno precedente'
                )}
                <small className="muted block">
                  senza dettaglio: l’anno prima non è nei mastrini
                </small>
              </>
            ) : (
              <>
                {item.invoiceId ? (
                  <Link to={`/tesoreria/fatture/${item.invoiceId}`}>
                    Doc. {item.documentNumber ?? '—'}
                  </Link>
                ) : (
                  <>Doc. {item.documentNumber ?? '—'}</>
                )}
                {item.documentDate && ` del ${formatDate(item.documentDate)}`}
                <small className="muted block">
                  {[
                    item.registrationDate && `registrata il ${formatDate(item.registrationDate)}`,
                    `importo ${formatMoney(item.amount)}`,
                    toCents(item.settled) !== 0 && `già pagato ${formatMoney(item.settled)}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </small>
              </>
            )}
          </td>
          <td className="numeric">
            {item.residual !== null && (
              <small className="muted">
                oggi{' '}
                {toCents(item.residual) === 0 ? (
                  <span className="pill ok">saldata</span>
                ) : (
                  formatMoney(item.residual)
                )}
              </small>
            )}
          </td>
          <td className="numeric">{formatMoney(item.open)}</td>
          <td>
            {item.settledOn ? (
              <span className="pill ok">✓ chiusa il {formatDate(item.settledOn)}</span>
            ) : (
              item.kind === 'documento' && <span className="muted small">non citata dopo</span>
            )}
          </td>
        </tr>
      ))}
    </>
  );
}

function toCsv(accounts: OpeningAccountResponse[]): string {
  const header = [
    'Ciclo',
    'Soggetto',
    'P.IVA / CF',
    'Anagrafica',
    'Documento',
    'Data documento',
    'Importo',
    'Già pagato',
    'Aperto',
    'Chiusa il',
    'Stato',
  ];
  const money = (value: string) => value.replace('.', ',');
  const lines = accounts.flatMap((account) => {
    const base = [
      account.cycle === 'attivo' ? 'Cliente' : 'Fornitore',
      account.name,
      account.vatNumber ?? account.taxCode ?? account.foreignId ?? '',
      account.subject?.name ?? '',
    ];
    const state = OPENING_STATE_LABELS[account.state];
    if (account.items.length === 0) {
      return [[...base, '', '', '', '', money(account.balance), '', state]];
    }
    return account.items.map((item) => [
      ...base,
      item.kind === 'ante' ? 'Saldo senza dettaglio' : (item.documentNumber ?? ''),
      item.documentDate ?? '',
      money(item.amount),
      money(item.settled),
      money(item.open),
      item.settledOn ?? '',
      state,
    ]);
  });
  return [header, ...lines]
    .map((line) =>
      line.map((cell) => (/[";\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell)).join(';'),
    )
    .join('\r\n');
}
